import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { brandSettingsService, DEFAULT_COLORS } from '../services/brandSettingsService';

const defaultBrand = {
  headerLogoUrl: null,
  footerLogoUrl: null,
  faviconUrl: null,
  bannerUrl: null,
  mobileBannerUrl: null,
  headerLogoScale: 100,
  footerLogoScale: 100,
  colors: DEFAULT_COLORS,
};

const BrandContext = createContext({
  ...defaultBrand,
  loading: true,
  refreshBrand: () => {},
});

/**
 * مزود سياق الهوية البصرية
 * يوفر: شعارات + بانر + أحجام + ألوان + حقن CSS Variables
 * يستخدم تخزيناً محلياً لظهور البانر والألوان فوراً دون وميض القيم السابقة
 */
export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(() => {
    const cached = brandSettingsService.getCachedBrand();
    return cached || defaultBrand;
  });
  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    const cached = brandSettingsService.getCachedBrand();
    if (cached?.colors) applyThemeColors(cached.colors);
    if (cached?.faviconUrl) updateFavicon(cached.faviconUrl);
  }, []);

  const loadBrand = useCallback(async () => {
    try {
      const settings = await brandSettingsService.getBrandSettings();
      setBrand(settings);

      if (settings.faviconUrl) {
        updateFavicon(settings.faviconUrl);
      }

      applyThemeColors(settings.colors);
    } catch (err) {
      console.error('❌ Failed to load brand settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrand();
  }, [loadBrand]);

  const refreshBrand = useCallback(async () => {
    setLoading(true);
    await loadBrand();
  }, [loadBrand]);

  return (
    <BrandContext.Provider value={{ ...brand, loading, refreshBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

/**
 * حقن ألوان الموقع كـ CSS Variables على :root
 */
function applyThemeColors(colors) {
  if (!colors) return;
  const root = document.documentElement;

  if (colors.primary) {
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-ring', colors.primary);
  }
  if (colors.secondary) {
    root.style.setProperty('--color-secondary', colors.secondary);
  }
  if (colors.text) {
    root.style.setProperty('--color-foreground', colors.text);
  }
  if (colors.background) {
    root.style.setProperty('--color-background', colors.background);
  }
  if (colors.button) {
    root.style.setProperty('--color-button', colors.button);
    // لون نص الأزرار - افتراضي أبيض إذا لم يُحدد
    root.style.setProperty('--color-button-foreground', '#FFFFFF');
  }
  if (colors.link) {
    root.style.setProperty('--color-link', colors.link);
  }
  if (colors.hover) {
    root.style.setProperty('--color-hover', colors.hover);
  }
  if (colors.accent) {
    root.style.setProperty('--color-accent', colors.accent);
  }
  if (colors.menuIcon) {
    root.style.setProperty('--color-menu-icon', colors.menuIcon);
  }
  if (colors.footer) {
    root.style.setProperty('--color-footer', colors.footer);
  }
  if (colors.heroCtaGradientStart) {
    root.style.setProperty('--color-hero-cta-gradient-start', colors.heroCtaGradientStart);
  }
  if (colors.heroCtaGradientEnd) {
    root.style.setProperty('--color-hero-cta-gradient-end', colors.heroCtaGradientEnd);
  }
}

/**
 * تحديث الفافيكون في الصفحة
 */
function updateFavicon(url) {
  if (!url) return;

  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;

  let appleLink = document.querySelector("link[rel='apple-touch-icon']");
  if (!appleLink) {
    appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleLink);
  }
  appleLink.href = url;
}

export function useBrand() {
  return useContext(BrandContext);
}

export default BrandContext;
